import React, { type ReactNode } from 'react';
import { defineMessages, useIntl, type IntlShape } from 'react-intl';

import { token } from '@atlaskit/tokens';
import { Box } from '@atlaskit/primitives/compiled/box';
import { cssMap, cx } from '@atlaskit/css';

import { useKeyboardShortcutsEnabled } from './useKeyboardShortcutsEnabled.ts';

export const isMac = (): boolean => {
	let userAgent = '';
	if (window?.__SSR_USER_AGENT__) {
		userAgent = window.__SSR_USER_AGENT__;
	} else {
		userAgent = window?.navigator?.userAgent ?? '';
	}
	return userAgent.indexOf('Mac') > -1;
};

const styles = cssMap({
	flexCenter: {
		display: 'flex',
		alignItems: 'center',
		paddingBlock: token('space.025'),
	},
	base: {
		display: 'inline-block',
		borderRadius: token('radius.small'),
		marginLeft: token('space.025'),
		marginRight: token('space.025'),
		paddingBlock: token('space.025'),
		paddingInline: token('space.100'),
	},
	contentBefore: {
		marginLeft: token('space.050'),
		marginRight: token('space.0'),
	},
	darkBackground: {
		backgroundColor: token('color.background.accent.gray.subtle'),
	},
	lightBackground: {
		backgroundColor: token('color.background.accent.gray.subtlest'),
	},
});

const i18n = defineMessages({
	keyboardShortcutShift: {
		id: 'shortcuts.shift',
		defaultMessage: 'Shift',
		description: 'Label for the Shift modifier key in keyboard shortcut visualizer components.',
	},
	keyboardShortcutCtrl: {
		id: 'shortcuts.ctrl',
		defaultMessage: 'Ctrl',
		description: 'Label for the Ctrl modifier key in keyboard shortcut visualizer components.',
	},
	keyboardShortcutCommandWin: {
		id: 'shortcuts.cmd.win',
		defaultMessage: 'Ctrl',
		description:
			'Label for the Cmd key on Windows (shown as Ctrl) in keyboard shortcut visualizer components.',
	},
	keyboardShortcutCommandMac: {
		id: 'shortcuts.cmd.mac',
		defaultMessage: 'Cmd',
		description:
			'Label for the Command (Cmd) modifier key on Mac in keyboard shortcut visualizer components.',
	},
	keyboardShortcutOptionMac: {
		id: 'shortcuts.opt.mac',
		defaultMessage: 'Opt',
		description:
			'Label for the Option (Opt) modifier key on Mac in keyboard shortcut visualizer components.',
	},
	keyboardShortcutOptionWin: {
		id: 'shortcuts.opt.win',
		defaultMessage: 'Alt',
		description:
			'Label for the Alt modifier key on Windows in keyboard shortcut visualizer components.',
	},
	keyboardShortcutPlus: {
		id: 'shortcuts.plus',
		defaultMessage: '+',
		description:
			'Plus sign used as a separator between keys in keyboard shortcut visualizer components.',
	},
	keyboardShortcutMinus: {
		id: 'shortcuts.minus',
		defaultMessage: '-',
		description:
			'Minus sign used as a separator between key sequences in keyboard shortcut visualizer components.',
	},
	keyboardShortcutEnter: {
		id: 'shortcuts.enter',
		defaultMessage: 'Enter',
		description: 'Display of a keyboard shortcut with the enter key on the keyboard',
	},
	keyboardShortcutMeta: {
		id: 'shortcuts.meta',
		defaultMessage: 'Meta',
		description: 'Display of a keyboard shortcut with the windows key on the keyboard',
	},
});

const processKey = (key, formatMessage) => {
	const mac = isMac();
	const lowerCaseKey = key.toLowerCase();

	switch (lowerCaseKey) {
		case 'shift':
			return '\u21E7'; // ⇧
		case 'cmd':
		case 'mod':
			return mac
				? '\u2318' // ⌘
				: formatMessage(i18n.keyboardShortcutCommandWin);
		case 'ctrl':
			return formatMessage(i18n.keyboardShortcutCtrl);
		case 'opt':
		case 'option':
			return mac
				? formatMessage(i18n.keyboardShortcutOptionMac)
				: formatMessage(i18n.keyboardShortcutOptionWin);
		case 'enter':
			return formatMessage(i18n.keyboardShortcutEnter);
		case 'meta':
			return formatMessage(i18n.keyboardShortcutMeta);
		case 'uparrow':
			return '\u2191'; // ↑
		case 'downarrow':
			return '\u2193'; // ↓
		case 'leftarrow':
			return '\u2190'; // ←
		case 'rightarrow':
			return '\u2192'; // →
		case 'plus':
			return '+';
		case 'minus':
			return '-';
		default:
			return key;
	}
};

const getShortcutStrings = (
	accelerator: string,
	formatMessage: IntlShape['formatMessage'],
): string[] => {
	const shortcutKeys = accelerator.includes(' ') ? accelerator.split(' ') : accelerator.split('+');
	return shortcutKeys.map((key) => {
		const processedKey = processKey(key, formatMessage);

		if (processedKey.length === 1) {
			return processedKey.toUpperCase();
		} else {
			return processedKey;
		}
	});
};

export const getShortcutString = (
	accelerator: string,
	formatMessage: IntlShape['formatMessage'],
): string => {
	return getShortcutStrings(accelerator, formatMessage).join(' ');
};

type ShortcutVisualizerProps = {
	shortcut: string;
	contentBefore?: ReactNode;
	isEditorShortcut?: boolean;
	isTooltip?: boolean;
};

export const ShortcutVisualizer = ({
	shortcut,
	contentBefore,
	isEditorShortcut,
	isTooltip = false,
}: ShortcutVisualizerProps): React.JSX.Element => {
	const [areKeyboardShortcutsEnabled] = useKeyboardShortcutsEnabled();
	const intl = useIntl();
	const shortcutStrings = getShortcutStrings(shortcut, intl.formatMessage);
	const shouldRenderShortcutSegment = areKeyboardShortcutsEnabled || isEditorShortcut;

	// This is going to be rendered in a <span> so no <div> should be used here.
	// Otherwise it will break HTML structure causing SSR hydration mismatch.
	return (
		<Box as="span" xcss={styles.flexCenter}>
			{contentBefore}
			{shouldRenderShortcutSegment &&
				shortcutStrings.map((shortcutSegment, index) => (
					<Box
						as="span"
						key={`${shortcutSegment}-${index}`}
						xcss={cx(
							styles.base,
							Boolean(contentBefore) && styles.contentBefore,
							isTooltip ? styles.darkBackground : styles.lightBackground,
						)}
					>
						{shortcutSegment}
					</Box>
				))}
		</Box>
	);
};

/**
 * Converts shortcut strings like "ctrl+[" to an array of strings like ["Ctrl", "["].
 *
 * This is used for mapping the shortcut constants in `confluence-shortcuts.ts` to the
 * format expected by the ADS Tooltip `shortcut` prop.
 *
 * It checks if keyboard shortcuts are enabled and returns undefined if they are not.
 *
 * @example
 * ```
 * const shortcut = useShortcutStrings({ shortcut: 'ctrl+[' });
 * <Tooltip content="My tooltip" shortcut={shortcut} />
 * ```
 */
export const useShortcutStrings = ({
	shortcut,
	isEditorShortcut = false,
}: {
	/**
	 * The shortcut string to be converted to an array of strings.
	 */
	shortcut?: string;
	/**
	 * If true, the shortcut will be returned even if the keyboard shortcuts preference is not enabled.
	 */
	isEditorShortcut?: boolean;
}): Array<string> | undefined => {
	const intl = useIntl();
	const [areKeyboardShortcutsEnabled] = useKeyboardShortcutsEnabled();

	if (!shortcut) {
		return undefined;
	}

	const shortcutStrings = getShortcutStrings(shortcut, intl.formatMessage);

	// Editor shortcuts are always enabled
	const shouldDisplayShortcut = areKeyboardShortcutsEnabled || isEditorShortcut;

	if (!shouldDisplayShortcut) {
		return undefined;
	}

	return shortcutStrings;
};
